import { useState } from 'react';

import { formatDuration, validateProgram } from '../core/validator';
import { isAppleMobile, useConnection } from '../store/connection';
import { useStore } from '../store/store';
import { DEVICE_WIFI } from '../transport/types';
import type { ConnectionState } from '../transport/types';
import { describeFrequency } from '../data/programs';
import { Modal } from './common';
import { StatRow, Visualizer } from './Visualizer';

const STATE_LABEL: Record<ConnectionState, string> = {
  unsupported: 'Not available',
  disconnected: 'Not connected',
  scanning: 'Looking…',
  connecting: 'Connecting…',
  discovering: 'Connecting…',
  connected: 'Connected',
  reconnecting: 'Reconnecting…',
};

const STATE_TONE: Record<ConnectionState, string> = {
  unsupported: 'bad',
  disconnected: 'neutral',
  scanning: 'ok',
  connecting: 'ok',
  discovering: 'ok',
  connected: 'good',
  reconnecting: 'warn',
};

export function Home({ onEdit }: { onEdit: () => void }) {
  const store = useStore();
  const conn = useConnection();
  const [showHelp, setShowHelp] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [paste, setPaste] = useState('');
  const [picking, setPicking] = useState(false);

  const favorites = store.programs.filter((p) => p.favorite);

  const program = store.current;
  const result = program ? validateProgram(program, store.profile.avoidBands) : null;
  const connected = conn.state === 'connected';

  // On an iPhone, Bluetooth is not a thing that can be fixed or retried — the
  // Wi-Fi route replaces it outright rather than sitting under an error.
  //
  // These two are deliberately different. A browser with no Web Bluetooth
  // starts on the MANUAL transport, so a check of "transport is bluetooth AND
  // unsupported" is false on exactly the phones that need the Wi-Fi card, and
  // the card never renders. Whether to EXPLAIN the situation depends only on
  // the browser; whether to DISABLE the connect button depends on the transport.
  const appleMobile = isAppleMobile();
  const bluetoothUnavailable = !conn.platform.supported;
  const btTransportBlocked = conn.transportId === 'web-bluetooth' && bluetoothUnavailable;

  /** Run something straight from the favourites strip. */
  const quickRun = async (p: typeof store.programs[number]) => {
    store.select(p.id);
    if (!connected) return;
    const r = validateProgram(p, store.profile.avoidBands);
    if (!r.canRun) return;
    await conn.sendProgram(p, { run: true });
    store.logSession(p, r.totalMin);
  };

  const runProgram = async () => {
    if (!program || !result?.canRun) return;
    await conn.sendProgram(program, { run: true });
    store.logSession(program, result.totalMin);
    if (conn.transportId === 'manual') setShowManual(true);
  };

  return (
    <>
      <Visualizer session={conn.session} />
      <StatRow session={conn.session} />

      <div className="card">
        <div className="card-head">
          <h3 className="card-title grow">Device</h3>
          <span className={`badge ${STATE_TONE[conn.state]}`}>{STATE_LABEL[conn.state]}</span>
        </div>

        {bluetoothUnavailable ? (
          appleMobile ? (
            <WifiRoute />
          ) : (
            <div className="banner">
              <span aria-hidden="true">ⓘ</span>
              <div>
                <strong>This browser can't do Bluetooth.</strong>
                <div className="faint" style={{ marginTop: 4 }}>
                  {conn.platform.reason}
                </div>
              </div>
            </div>
          )
        ) : null}

        <div className="row wrap">
          {connected ? (
            <>
              <button className="btn" onClick={() => void conn.disconnect()}>
                Disconnect
              </button>
              <button className="btn small" onClick={() => void conn.refreshStatus()}>
                Refresh
              </button>
              <button className="btn small" onClick={() => void conn.runLast()}>
                Run last
              </button>
            </>
          ) : (
            // The button stays even on an iPhone. Without Web Bluetooth the
            // active transport is the copy-and-paste one, and this is how that
            // gets started — hiding it would remove the fallback that works
            // when the device's Wi-Fi does not.
            <>
              <button
                className="btn primary grow"
                disabled={btTransportBlocked}
                onClick={() => void conn.connect()}
              >
                {conn.transportId === 'manual' ? 'Start copy & paste' : 'Connect'}
              </button>
              <button className="btn small" onClick={() => setShowHelp(true)}>
                How?
              </button>
            </>
          )}
        </div>

        <div className="faint" style={{ marginTop: 10 }}>
          {conn.firmware ? (
            <>
              Firmware {conn.firmware} · auto-start {conn.autorun ? 'on' : 'off'}
              {conn.savedPrograms.length > 0 ? ` · on device: ${conn.savedPrograms.join(', ')}` : ''}
            </>
          ) : (
            <button
              className="btn small ghost"
              style={{ padding: 0 }}
              onClick={() => setShowHelp(true)}
            >
              How does connecting work?
            </button>
          )}
        </div>

        {conn.lastError ? (
          <div className="issue error" style={{ marginTop: 10 }}>
            {conn.lastError}
          </div>
        ) : null}

        {conn.transportId === 'manual' && connected ? (
          <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
            <div className="field-label" style={{ marginBottom: 5 }}>
              Paste what the device replied
            </div>
            <div className="row">
              <input
                type="text"
                placeholder="RUNNING | cycle 1/4 | TREATMENT…"
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                aria-label="Device response"
              />
              <button
                className="btn small"
                disabled={!paste.trim()}
                onClick={() => {
                  conn.feedManual(paste.trim());
                  setPaste('');
                }}
              >
                Apply
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {conn.session.running ? (
        <div className="card">
          <div className="card-head">
            <h3 className="card-title grow">Running</h3>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            {conn.session.totalRemaining > 0
              ? `About ${formatDuration(conn.session.totalRemaining)} left.`
              : 'The device is running on its own.'}
          </p>
          <button className="btn danger" onClick={() => void conn.stop()}>
            Stop
          </button>
        </div>
      ) : null}

      {favorites.length > 0 ? (
        <>
          <div className="section-label">Favourites</div>
          {favorites.map((fav) => (
            <div className="card" key={fav.id}>
              <div className="row">
                <button
                  className={`play-btn ${connected ? '' : 'idle'}`}
                  aria-label={`Play ${fav.title ?? fav.name}`}
                  onClick={() => void quickRun(fav)}
                >
                  ▶
                </button>
                <div className="grow">
                  <strong>{fav.title ?? fav.name}</strong>
                  <div className="faint mono">{describeFrequency(fav.cycles)}</div>
                </div>
                <button
                  className="star-btn on"
                  aria-label="Remove from favourites"
                  onClick={() => store.toggleFavorite(fav.id)}
                >
                  ♥
                </button>
              </div>
            </div>
          ))}
        </>
      ) : null}

      <div className="section-label">Last used session</div>
      <div className="card">
        <div className="card-head">
          <h3 className="card-title grow">
            {program ? program.title ?? program.name : 'Nothing chosen yet'}
          </h3>
          {program ? (
            <button
              className={`star-btn ${program.favorite ? 'on' : ''}`}
              aria-label={program.favorite ? 'Remove from favourites' : 'Add to favourites'}
              onClick={() => store.toggleFavorite(program.id)}
            >
              {program.favorite ? '♥' : '♡'}
            </button>
          ) : null}
        </div>

        {program && result ? (
          <>
            <div className="row wrap muted" style={{ marginBottom: 10 }}>
              <span className="mono">{describeFrequency(program.cycles)}</span>
              <span aria-hidden="true">·</span>
              <span className="mono">{formatDuration(result.totalMin)}</span>
            </div>
            <div className="row wrap">
              <button
                className="btn primary grow"
                disabled={!result.canRun || conn.sending !== null || !connected}
                onClick={() => void runProgram()}
              >
                {conn.sending
                  ? `Sending ${conn.sending.sent}/${conn.sending.total}…`
                  : '▶  Send and start'}
              </button>
              <button className="btn" onClick={() => setPicking(true)}>
                Select
              </button>
              <button className="btn small" onClick={onEdit}>
                Edit
              </button>
            </div>
            <p className="faint" style={{ marginTop: 10, marginBottom: 0 }}>
              Once it starts you can close the app — the device keeps going on its own.
            </p>
          </>
        ) : (
          <div className="row wrap">
            <button className="btn primary" onClick={() => setPicking(true)}>
              Select a program
            </button>
          </div>
        )}
      </div>

      {picking ? (
        <Modal title="Choose a program" onClose={() => setPicking(false)}>
          {favorites.length > 0 ? (
            <>
              <div className="section-label" style={{ marginTop: 0 }}>
                Favourites
              </div>
              <div className="stack">
                {favorites.map((p) => (
                  <button
                    key={p.id}
                    className="btn"
                    style={{ textAlign: 'left' }}
                    onClick={() => {
                      store.select(p.id);
                      setPicking(false);
                    }}
                  >
                    ♥ {p.title ?? p.name}
                    <span className="faint"> · {describeFrequency(p.cycles)}</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          <div className="section-label">Everything</div>
          <div className="stack">
            {store.programs.map((p) => (
              <button
                key={p.id}
                className="btn"
                style={{ textAlign: 'left' }}
                onClick={() => {
                  store.select(p.id);
                  setPicking(false);
                }}
              >
                {p.title ?? p.name}
                <span className="faint"> · {describeFrequency(p.cycles)}</span>
              </button>
            ))}
          </div>
        </Modal>
      ) : null}

      {showHelp ? <ConnectHelp onClose={() => setShowHelp(false)} /> : null}

      {showManual ? (
        <ManualSend
          commands={conn.manualBuffer}
          rxUuid={conn.deviceConfig.rxUuid}
          onClose={() => setShowManual(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The iPhone route: the device's own Wi-Fi.
 *
 * Not an error message. On an iPhone this IS the way the device is controlled,
 * so it reads as the normal path rather than as Bluetooth having failed.
 *
 * The link is a plain navigation on purpose. A secure page is allowed to send
 * you to an insecure address; it is not allowed to fetch from one. So the app
 * can hand you over to the device but can never drive it directly, and no
 * amount of cleverness here changes that.
 */
function WifiRoute() {
  const [copied, setCopied] = useState(false);

  const copyPass = async () => {
    try {
      await navigator.clipboard?.writeText(DEVICE_WIFI.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — it is on screen and short enough to type.
    }
  };

  return (
    <>
      <div className="banner">
        <span aria-hidden="true">ⓘ</span>
        <div>
          <strong>Use the device's own Wi-Fi</strong>
          <div className="faint" style={{ marginTop: 4 }}>
            Safari can't use Bluetooth — Apple has never allowed it. The device makes its own
            network instead, so this works anywhere, with no internet.
          </div>
        </div>
      </div>

      <ol className="caveat-list">
        <li>Press and hold the Wi-Fi tile in Control Centre.</li>
        <li>
          Tap <strong>{DEVICE_WIFI.ssid}</strong>. Password{' '}
          <span className="mono">{DEVICE_WIFI.password}</span> — only needed the first time.
        </li>
        <li>The page opens by itself.</li>
      </ol>

      <div className="row wrap">
        <a className="btn primary grow" href={DEVICE_WIFI.url} style={{ textAlign: 'center' }}>
          Open the device page
        </a>
        <button className="btn small" onClick={() => void copyPass()}>
          {copied ? 'Copied' : 'Copy password'}
        </button>
      </div>

      <p className="faint" style={{ marginTop: 10, marginBottom: 0 }}>
        Once you start a program the device switches its Wi-Fi off and your phone reconnects to
        its usual network on its own. The program keeps running. Needs firmware 4.3 or newer.
      </p>
    </>
  );
}

/**
 * The other iPhone route, for firmware 4.2 without Wi-Fi.
 *
 * Safari has no Web Bluetooth, so on an iPhone the only way to reach the device
 * is a general-purpose Bluetooth terminal app. Firmware 4.2 accepts a whole
 * program in one paste, which is what makes this bearable — before that it was
 * one write per line and nobody was going to do twenty-five of those at night.
 */
function ManualSend({
  commands,
  rxUuid,
  onClose,
}: {
  commands: string[];
  rxUuid: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // The trailing newline matters: some terminal apps strip it, and the firmware
  // treats the last line the same either way, but it makes the paste look right.
  const blob = commands.join('\n') + '\n';

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(blob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the text is on screen and can be selected by hand.
    }
  };

  return (
    <Modal title="Send from an iPhone" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        Copy this, then paste it into a Bluetooth terminal app. The whole program goes across in
        one paste.
      </p>

      <pre className="pre">{commands.join('\n')}</pre>

      <button className="btn primary" style={{ width: '100%' }} onClick={() => void copy()}>
        {copied ? 'Copied' : `Copy all ${commands.length} commands`}
      </button>

      <hr className="divider" />

      <div className="field-label" style={{ marginBottom: 6 }}>
        nRF Toolbox — the easier one
      </div>
      <ol className="caveat-list">
        <li>Open <strong>UART</strong>, then <strong>Connect</strong>, and pick PEMF-Controller.</li>
        <li>Paste into the text box and send.</li>
        <li>
          Turn on the <strong>newline</strong> or <strong>EOL</strong> option if it has one, so the
          line breaks survive.
        </li>
      </ol>

      <div className="field-label" style={{ marginTop: 12, marginBottom: 6 }}>
        LightBlue — more fiddly
      </div>
      <ol className="caveat-list">
        <li>Connect to PEMF-Controller and open the characteristic ending <strong>0002</strong>.</li>
        <li>
          Tap <strong>Write new value</strong> and switch the format from Hex to{' '}
          <strong>UTF-8 String</strong> — this is the step everyone misses.
        </li>
        <li>Paste and send.</li>
      </ol>

      <p className="faint" style={{ marginBottom: 0 }}>
        Needs firmware 4.2 or newer. The write characteristic is{' '}
        <span className="mono">{rxUuid}</span>.
      </p>
    </Modal>
  );
}

function ConnectHelp({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="How connecting works" onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        There's no pairing step. The device isn't in your phone's Bluetooth settings and doesn't
        need to be — this app talks to it directly.
      </p>
      <ol className="caveat-list">
        <li>Switch the device on. It starts announcing itself straight away.</li>
        <li>Tap <strong>Connect</strong>. Your browser shows a short list of nearby devices.</li>
        <li>Pick <strong>PEMF-Controller</strong>.</li>
      </ol>
      <p className="muted">
        It keeps announcing itself for as long as it's powered on — there's no time limit and
        nothing to re-pair. If you disconnect, it goes straight back to announcing, so you can
        reconnect whenever.
      </p>
      <hr className="divider" />
      <p className="faint" style={{ marginBottom: 0 }}>
        Bluetooth needs Chrome or Edge on Android, Windows, Mac or Linux. iPhones and iPads can't
        do it from Safari at all — use the Bluefy browser, or switch to Manual under Settings and
        copy the commands across by hand.
      </p>
    </Modal>
  );
}
